// Shared types and configuration for the onboarding wizard

export interface DomainData {
  name: string;
  url: string;
  type: string;
  created: boolean;
  website_id: string;
}
export function normalizeDomain(input: string): string {
  let s = (input || '').trim().toLowerCase();
  s = s.replace(/^https?:\/\//, ''); // drop protocol
  s = s.split(/[/?#]/)[0]; // drop path / query / fragment
  s = s.replace(/\.+$/, ''); // guard stray trailing dot
  return s ? `https://${s}` : '';
}

// Normalize a funnel page URL: force https, lowercase the host, keep the path/query.
export function normalizePageUrl(input: string): string {
  const s = (input || '').trim();
  if (!s) return '';
  const withoutScheme = s.replace(/^https?:\/\//i, '');
  const slash = withoutScheme.search(/[/?#]/);
  const host = (slash === -1 ? withoutScheme : withoutScheme.slice(0, slash)).toLowerCase().replace(/\.+$/, '');
  const rest = slash === -1 ? '' : withoutScheme.slice(slash);
  return host ? `https://${host}${rest}` : '';
}

// Host of a page/domain URL: lowercase, no scheme/path/port/www.
// Mirrors normalize_domain_host() on the server so comparisons agree.
export function domainHost(input: string): string {
  return normalizePageUrl(input)
    .replace(/^https:\/\//, '')
    .split(/[/?#]/)[0]
    .replace(/:\d+$/, '')
    .replace(/^www\./, '');
}

export interface SubdomainRow {
  id: string;
  name: string;
  platform: string;
  // Shopify related domains install the Smart Code via the Public App
  shopifyConnected?: boolean;
  shopifyStore?: string;
}

export interface FunnelData {
  name: string;
  pages: string[];
  created: boolean;
  funnel_id?: string;
  savedUrls?: string[];
}

export interface WebhookRow {
  id: string;
  name: string;
}

export interface OnboardingData {
  domain: DomainData;
  scriptVerified: boolean;
  scriptBypassed: boolean;
  shopifyConnected: boolean;
  subdomains: SubdomainRow[];
  funnel: FunnelData;
  webhooks: WebhookRow[];
  currentStep: number;
}

export interface StepProps {
  data: OnboardingData;
  update: (patch: Partial<OnboardingData>) => void;
  errors: Record<string, string>;
  goToDashboard?: (to?: string) => void;
  goNext?: () => void;
  // True while the step's Next action is saving to the server — steps should lock their inputs
  saving?: boolean;
}

export const defaultOnboardingData: OnboardingData = {
  domain: {
    name: '',
    url: '',
    type: '',
    created: false,
    website_id: '',
  },
  scriptVerified: false,
  scriptBypassed: false,
  shopifyConnected: false,
  subdomains: [],
  funnel: {
    name: '',
    pages: [''],
    created: false,
    funnel_id: '',
    savedUrls: [],
  },
  webhooks: [],
  currentStep: 0,
};

// Platform options drive the tailored install hint in Step 2
// Both sets are grouped on purpose: Mantine v6 always renders grouped items before ungrouped
// ones, so the only way to keep "Frontend Framework" last is to give the others a group too.
export const DOMAIN_TYPES = [
  { value: 'custom', label: 'Custom HTML / PHP', group: 'Common Platforms' },
  { value: 'ghl', label: 'GHL (GoHighLevel)', group: 'Common Platforms' },
  { value: 'shopify', label: 'Shopify', group: 'Common Platforms' },
  { value: 'wordpress', label: 'WordPress', group: 'Common Platforms' },
  { value: 'emdash', label: 'EmDash CMS', group: 'Common Platforms' },
  { value: 'react', label: 'ReactJS', group: 'Frontend Framework' },
  { value: 'vue', label: 'VueJS', group: 'Frontend Framework' },
  { value: 'next', label: 'NextJS', group: 'Frontend Framework' },
  { value: 'angular', label: 'AngularJS', group: 'Frontend Framework' },
  { value: 'astro', label: 'Astro', group: 'Frontend Framework' },
  { value: 'framework_other', label: 'Other', group: 'Frontend Framework' },
];

export const DOMAIN_TYPE_HINTS: Record<string, string> = {
  custom: 'Install this Smart Code inside the <head> section of your website to enable tracking across your entire site. If you only want to track specific pages, install the Smart Code only on those pages instead.',
  react: 'Add this Smart Code to public/index.html inside <head>, so it loads on every route.',
  vue: 'Add this Smart Code to public/index.html inside <head>, so it loads on every route.',
  next: 'Add this Smart Code in your _document.js / layout <Head> so it renders on every page.',
  angular: 'Add this Smart Code to src/index.html inside <head>, so it loads on every route.',
  framework_other: 'Install this Smart Code inside the <head> section of your website to enable tracking across your entire site. If you only want to track specific pages, install the Smart Code only on those pages instead.',
  ghl: 'Add this in Sites → Settings → Head Tracking Code (or the funnel/site custom code header).',
  shopify: 'Shopify uses the Visiopt Public App — connect your store below and the Smart Code is installed and kept up to date for you. No code to paste.',
  wordpress: "Paste this inside your theme's <head> section or use a header-scripts plugin.",
  astro: 'Add this Smart Code to your astro.config.mjs, so it loads on every page.',
  emdash: 'EmDash CMS installs the Smart Code through the Visiopt plugin — add it in your site head settings so it loads across your whole site.',
};

// Platform-specific special cases surfaced in Step 2 [2d]
// Shopify is intentionally absent — the Public App installs the code, there is nothing to paste.
export const DOMAIN_TYPE_SPECIAL: Record<string, string[]> = {
  ghl: [
    'Whole account: Settings → Business Profile → Tracking Code (Head).',
    'Single funnel/site: Funnels → Settings → Tracking Code → Head.',
    'Order/checkout steps: add the code to each funnel step that should be tracked.',
  ],
  wordpress: [
    'Recommended: use a header-scripts plugin (e.g. "Insert Headers and Footers") so updates survive theme changes.',
    'Page builders (Elementor, Divi, Beaver): also add it in the builder\'s custom-code / head settings if pages render outside the theme head.',
  ],
  react: ['Server-rendered/SSR pages: also inject the Smart Code server-side so it is present in the initial HTML.'],
  next: ['Use the App Router <head> in layout.tsx (or _document.js on the Pages Router) so it renders on every route, including SSR.'],
  custom: ['Install this Smart Code in the <head> section of your domain.'],
};

// Map an onboarding platform to the website record's numeric framework fields.
// front_end_framework: 0=HTML/PHP, 1=Frontend Framework, 2=Shopify, 3=GHL, 4=WordPress, 5=EmDash CMS
// framework_type (when front_end_framework=1): 1=React, 2=Angular, 3=Vue, 4=Next, 5=Other, 6=Astro
export function platformToFramework(type: string): { front_end_framework: string; framework_type: string } {
  const map: Record<string, [string, string]> = {
    custom: ['0', '1'],
    react: ['1', '1'],
    angular: ['1', '2'],
    vue: ['1', '3'],
    next: ['1', '4'],
    astro: ['1', '6'],
    framework_other: ['1', '5'],
    shopify: ['2', '1'],
    ghl: ['3', '1'],
    wordpress: ['4', '1'],
    emdash: ['5', '1'],
  };
  const [front_end_framework, framework_type] = map[type] || ['0', '1'];
  return { front_end_framework, framework_type };
}

// Inverse: map the website record's numeric framework fields back to a platform value
export function frameworkToPlatform(frontEndFramework: string, frameworkType: string): string {
  const fef = String(frontEndFramework || '0');
  const ft = String(frameworkType || '1');
  if (fef === '2') return 'shopify';
  if (fef === '3') return 'ghl';
  if (fef === '4') return 'wordpress';
  if (fef === '5') return 'emdash';
  if (fef === '1') {
    return { '1': 'react', '2': 'angular', '3': 'vue', '4': 'next', '5': 'framework_other', '6': 'astro' }[ft] || 'framework_other';
  }
  return 'custom';
}


// Supported webhooks for Step 5 — logos live in public/img/cart-icons
export interface WebhookOption {
  value: string;
  label: string;
  logo: string;
  // Backend processor + capture slug; default to value / webhook_capture_<value>
  processor?: string;
  slug?: string;
}

// Resolve the backend processor/slug for a webhook option
export const webhookProcessor = (o: WebhookOption) => o.processor || o.value;
export const webhookSlug = (o: WebhookOption) => o.slug || `webhook_capture_${o.value}`;

const ICON = 'img/cart-icons';

export const WEBHOOK_GROUPS: { title: string; items: WebhookOption[] }[] = [
  {
    title: 'Payment Processors',
    items: [
      { value: 'authorize', label: 'Authorize.net', logo: `${ICON}/authorize.png`, slug: 'webhook_capture_authorize_net' },
      { value: 'chargebee', label: 'Chargebee', logo: `${ICON}/chargebee.png` },
      { value: 'easypaydirect', label: 'Easy Pay Direct', logo: `${ICON}/easypaydirect.png` },
      { value: 'gocardless', label: 'Go Cardless', logo: `${ICON}/gocardless.png` },
      { value: 'hitpay', label: 'Hit Pay', logo: `${ICON}/hitpay.png` },
      { value: 'housecallpro', label: 'Housecall pro', logo: `${ICON}/housecallpro.png` },
      { value: 'merchantx', label: 'Merchantx', logo: `${ICON}/merchantx.png` },
      { value: 'paypal', label: 'Paypal Classic IPN', logo: `${ICON}/paypal.png` },
      { value: 'recurly', label: 'Recurly', logo: `${ICON}/recurly.png` },
      { value: 'stripe', label: 'Stripe', logo: `${ICON}/stripe.png` },
    ],
  },
  {
    title: 'Shopping Carts',
    items: [
      { value: 'buygoods', label: 'Buy Goods', logo: `${ICON}/buygoods.png` },
      { value: 'calendly', label: 'Calendly', logo: `${ICON}/calendly.png` },
      { value: 'clickbank', label: 'Click Bank', logo: `${ICON}/clickbank.png` },
      { value: 'clickfunnels', label: 'ClickFunnels', logo: `${ICON}/clickfunnels.png`, slug: 'webhook_capture_clickfunnel' },
      { value: 'digistore24', label: 'Digistore24', logo: `${ICON}/digistore24.png` },
      { value: 'gohighlevel', label: 'Go High Level', logo: `${ICON}/gohighlevel.png` },
      { value: 'hubspot', label: 'HubSpot', logo: `${ICON}/hubspot.png` },
      { value: 'kajabi', label: 'Kajabi', logo: `${ICON}/kajabi.png` },
      { value: 'kartra', label: 'Kartra', logo: `${ICON}/kartra.png`, processor: 'katra', slug: 'webhook_capture_katra' },
      { value: 'konnektive', label: 'Konnektive', logo: `${ICON}/konnektive.png` },
      { value: 'ontraport', label: 'Ontraport', logo: `${ICON}/ontraport.png` },
      { value: 'paykickstart', label: 'Paykickstart', logo: `${ICON}/paykickstart.png`, processor: 'paykickstartes' },
      { value: 'samcart', label: 'Sam Cart', logo: `${ICON}/samcart.png` },
      { value: 'shopify', label: 'Shopify', logo: `${ICON}/shopify.png` },
      { value: 'stealthseminar', label: 'Stealth Seminar', logo: `${ICON}/stealthseminar.png` },
      { value: 'thrivecart', label: 'Thrive Cart', logo: `${ICON}/thrivecart.png`, slug: 'webhook_capture_thrive_cart' },
      { value: 'typeform', label: 'Typeform', logo: `${ICON}/typeform.png` },
      { value: 'ultracart', label: 'UltraCart', logo: `${ICON}/ultracart.png` },
      { value: 'woocommerce', label: 'Woocommerce', logo: `${ICON}/woocommerce.png` },
      { value: 'youform', label: 'Youform', logo: `${ICON}/youform.png` },
    ],
  },
];

// Custom integration is offered separately from the cart/processor grids
export const CUSTOM_INTEGRATION: WebhookOption = {
  value: 'customintegration',
  label: 'Custom Integration',
  logo: '',
  processor: 'custom',
  slug: 'webhook_capture_custom',
};

// Flat lookup for label/logo by value (includes custom integration)
export const WEBHOOK_INDEX: Record<string, WebhookOption> = WEBHOOK_GROUPS.reduce(
  (acc, g) => {
    g.items.forEach((it) => (acc[it.value] = it));
    return acc;
  },
  {} as Record<string, WebhookOption>
);
WEBHOOK_INDEX[CUSTOM_INTEGRATION.value] = CUSTOM_INTEGRATION;

// Per-integration setup-time estimate (minutes). Config-driven so it can be tuned later.
export const estMinutes = (id: string): number =>
  ({ shopify: 3, wordpress: 3, woocommerce: 3, customintegration: 5 } as Record<string, number>)[id] ?? 2;

// Total setup-time range for a set of selected integrations
export const totalEstRange = (ids: string[]): { low: number; high: number } => {
  const sum = ids.reduce((a, id) => a + estMinutes(id), 0);
  return { low: sum, high: Math.max(sum, Math.round(sum * 1.5)) };
};

// Login / dashboard URL opened in a new tab during guided setup
export const INTEGRATION_LOGIN: Record<string, string> = {
  authorize: 'https://login.authorize.net/',
  chargebee: 'https://app.chargebee.com/login',
  easypaydirect: 'https://www.easypaydirect.com/login/',
  hitpay: 'https://dashboard.hit-pay.com/login',
  housecallpro: 'https://pro.housecallpro.com/app/log_in',
  gocardless: 'https://manage.gocardless.com/sign-in',
  merchantx: 'https://www.merchantx.com/',
  paypal: 'https://www.paypal.com/signin',
  recurly: 'https://app.recurly.com/login',
  stripe: 'https://dashboard.stripe.com/login',
  buygoods: 'https://www.buygoods.com/login',
  calendly: 'https://calendly.com/login',
  clickbank: 'https://accounts.clickbank.com/login.htm',
  clickfunnels: 'https://app.clickfunnels.com/users/sign_in',
  digistore24: 'https://www.digistore24.com/en/login',
  gohighlevel: 'https://app.gohighlevel.com/',
  hubspot: 'https://app.hubspot.com/login',
  typeform: 'https://admin.typeform.com/login',
  kajabi: 'https://app.kajabi.com/login',
  kartra: 'https://app.kartra.com/login',
  konnektive: 'https://crm.konnektive.com/',
  ontraport: 'https://app.ontraport.com/',
  paykickstart: 'https://app.paykickstart.com/sign-in',
  samcart: 'https://app.samcart.com/auth/login',
  shopify: 'https://accounts.shopify.com/store-login',
  stealthseminar: 'https://stealthseminarapp.com/',
  ultracart: 'https://secure.ultracart.com/merchant/login/',
  thrivecart: 'https://thrivecart.com/signin/',
  woocommerce: 'https://wordpress.com/log-in',
  youform: 'https://app.youform.com/login',
};

// A single guided-setup step for an integration
export interface SetupStep {
  text: string; // {X} is replaced with the integration label
  points?: string[]; // optional sub-steps rendered as a bullet list
  code?: string; // optional copyable code block (sample payload, snippet)
  image?: string; // screenshot path; empty shows a placeholder box
  copyUrl?: boolean; // render the read-only webhook URL + copy button on this step
  docUrl?: string; // optional link to the platform's official documentation
}

// Generic fallback used when an integration has no custom steps defined yet
export const DEFAULT_SETUP_STEPS: SetupStep[] = [
  { text: 'Open your {X} account settings.' },
  { text: 'Navigate to the Webhooks / Integrations area.' },
  { text: 'Create a new outbound webhook.' },
  { text: 'Paste your Visiopt webhook URL.', copyUrl: true },
  { text: 'Select the events you want to send to Visiopt.' },
  { text: 'Save and activate the webhook.' },
];

// Per-integration steps. Add real text + screenshots per webhook here;
// integrations not listed fall back to DEFAULT_SETUP_STEPS.
export const SETUP_STEPS_BY_INTEGRATION: Record<string, SetupStep[]> = {
  authorize: [
    { text: 'Log in to Authorize.net and click the "Settings" link in the left sidebar.', image: 'img/webhook-setup/authorize/step1.png' },
    { text: 'In the "Business Settings" section, click the "Webhooks" link at the bottom.', image: 'img/webhook-setup/authorize/step2.png' },
    { text: 'Click "Add Endpoint".', image: 'img/webhook-setup/authorize/step3.png' },
    {
      text: 'Configure the endpoint as shown, then save:',
      points: [
        'Enter a name for the webhook.',
        'Paste in the URL copied from Visiopt.',
        'Set the status to Active.',
        'Select all events.',
      ],
      image: 'img/webhook-setup/authorize/step4.png',
    }
  ],
  chargebee: [
    { text: 'Log in to Chargebee and go to Settings > Configure Chargebee in the left sidebar.', image: 'img/webhook-setup/chargebee/step1.png' },
    { text: 'Scroll down and open Webhooks section.', image: 'img/webhook-setup/chargebee/step2.png' },
    { text: 'Click the "Add a webhook" button.', image: 'img/webhook-setup/chargebee/step3.png' },
    {
      text: 'Configure the endpoint as shown, then save:',
      points: [
        'Enter a name for the webhook.',
        'Paste in the URL copied from Visiopt.',
        'Select "Payment Succeeded" as Events to Send.',
      ],
      image: 'img/webhook-setup/chargebee/step4.png',
    }
  ],
  easypaydirect: [
    { text: 'Log in to Easy Pay Direct and in the sidebar, Under the Options section click on the Settings.', image: 'img/webhook-setup/easypaydirect/step1.png' },
    { text: 'In the Transactions Options section click on the "Webhooks" link.', image: 'img/webhook-setup/easypaydirect/step2.png' },
    { text: 'Click on the "Create" button.', image: 'img/webhook-setup/easypaydirect/step3.png' },
    { text: 'Select the "transaction.sale.success" event and click on the Save changes button.', image: 'img/webhook-setup/easypaydirect/step4.png',
    }
  ],
  hitpay: [
    { text: 'Log in to hitpay and go to Developers > Webhook Endpoints.', image: 'img/webhook-setup/hitpay/step1.png' },
    { text: 'Click the "New webhook" button.', image: 'img/webhook-setup/hitpay/step2.png' },
    {
      text: 'Configure the endpoint as shown, then save:',
      points: [
        'Enter a name for the webhook.',
        'Paste in the URL copied from Visiopt.',
        'Select "Created" Event from Charge section.',
        'Click Save button.'
      ],
      image: 'img/webhook-setup/hitpay/step3.png',
    }
  ],
  housecallpro: [
    { text: 'Log in to Housecall Pro then go to the "My app menu" then click "Go To app store".', image: 'img/webhook-setup/housecallpro/step1.png' },
    { text: 'Then in the Webhooks section click on the "Learn More" button.', image: 'img/webhook-setup/housecallpro/step2.png' },
    { text: 'Then toggle the button to "active" status.', image: 'img/webhook-setup/housecallpro/step3.png' },
    { text: 'Then paste the Visiopt webhook generated url and click Save.', image: 'img/webhook-setup/housecallpro/step4.png',
    },
    { text: 'After that enable the "job.paid" option (only this one).', image: 'img/webhook-setup/housecallpro/step5.png',
    }
  ],
  gocardless: [
    { text: 'Log in to GoCardless and go to Developers > Create > Webhook.', image: 'img/webhook-setup/gocardless/step1.png' },
    {
      text: 'Configure the endpoint as shown, then save:',
      points: [
        'Enter a name for the webhook.',
        'Paste in the URL copied from Visiopt.',
        'Hit "Create Webhook Endpoint".',
      ],
      image: 'img/webhook-setup/gocardless/step2.png',
    }
  ],
  merchantx: [
    { text: 'Log in to Merchant X and from the Settings > Webhooks page, click the "Add Endpoint" button.' },
    {
      text: 'Paste in the URL copied from Visiopt, then save:',
      points: [
        'Select all the event types you want to be notified of.',
        'As soon as the URL is saved you will start receiving events — there is no further setup required.',
      ],
      copyUrl: true,
    },
  ],
  paypal: [
    { text: 'Log in to PayPal Dashboard. Find the "Edit Instant Payment Notification (IPN)" settings. Then paste in the URL copied from Visiopt, select "Receive IPN messages (enabled)" and hit save.', image: 'img/webhook-setup/paypal/step1.png' }
  ],
  recurly: [
    { text: 'Log in to Recurly and in the left side bar go to Integrations > Webhooks > Configure.', image: 'img/webhook-setup/recurly/step1.png' },
    {
      text: 'Configure the endpoint as shown, then save:',
      points: [
        'Enter a name for the webhook.',
        'Paste in the URL copied from Visiopt.',
        'Select XML as the format option.'
      ],
      image: 'img/webhook-setup/recurly/step2.png',
    },
    { text: 'Also check the "successful_payment" option (only this one) and click save.', image: 'img/webhook-setup/recurly/step3.png' },
  ],
  stripe: [
    {
      text: 'If you entered your Stripe secret key on the previous screen and it confirmed the webhook was created, you can skip these steps. If you left the secret key blank, or automatic creation failed, follow these steps to add the webhook in Stripe manually.',
    },
    { text: 'Log in to Stripe and click "Developers" in the top menu.', image: 'img/webhook-setup/stripe/step1.png' },
    { text: 'Select "Webhooks" from the Developers menu.', image: 'img/webhook-setup/stripe/step2.png' },
    { text: 'Click the "+ Add endpoint" button.', image: 'img/webhook-setup/stripe/step3.png' },
    {
      text: 'Paste the Visiopt webhook URL shown above into the "Endpoint URL" field.',
      image: 'img/webhook-setup/stripe/step4.png',
    },
    {
      text: 'Click "+ Select events" and choose exactly one event, then save:',
      points: [
        'Open "Payment intent" and select payment_intent.succeeded.',
        'If payment_intent.succeeded is unavailable (older Stripe API versions), select charge.succeeded instead.',
        'Do not select both — that records the same transaction twice.',
        'Click "Add events", then "Add endpoint" to save.',
      ],
      image: 'img/webhook-setup/stripe/step5.png',
    },
  ],
  buygoods: [
    { text: 'Log in to Buy Goods the select Setup > Products.', image: 'img/webhook-setup/buygoods/step1.png' },
    { text: 'To track a specific product, click on the desired product.', image: 'img/webhook-setup/buygoods/step2.png' },
    { text: 'Then select the "IPN" tab, and paste in the Visiopt URL to the "Order URL (IPN):" field.', image: 'img/webhook-setup/buygoods/step3.png' },
    { text: 'To track all your products with a single webhook, set up a Global webhook as shown below.', image: 'img/webhook-setup/buygoods/step4.png' }
  ],
  calendly: [
    { text: 'Copy your Visiopt webhook URL — you will pass it to Calendly through their API.', copyUrl: true },
    {
      text: 'Calendly does not expose webhooks in its dashboard. Webhook subscriptions are created through the Calendly API, using their "Create Webhook Subscription" endpoint.',
      points: [
        'When you create the webhook subscription, set the request\'s "url" field to the Visiopt webhook URL you copied above — that is where Calendly will send your events.',
        'Choose the events you want to receive (for example invitee.created) and the scope (user or organization).',
        'Follow the official Calendly documentation below for the exact request format and authentication.',
      ],
      docUrl: 'https://developer.calendly.com/receive-data-from-scheduled-events-in-real-time-with-webhook-subscriptions',
    },
  ],
  clickbank: [
    { text: 'Log in to Click Bank and click on the Accounts menu from the sidebar.', image: 'img/webhook-setup/clickbank/step1.png' },
    { text: 'Click on the Seller account.', image: 'img/webhook-setup/clickbank/step2.png' },
    { text: 'Click on the Vendor Settings link.', image: 'img/webhook-setup/clickbank/step3.png' },
    { text: 'Click My Site.', image: 'img/webhook-setup/clickbank/step4.png' },
    { text: 'In the Advanced Tools section, click on Edit.', image: 'img/webhook-setup/clickbank/step5.png' },
    { text: 'Enter a secret key (it can be any alphanumeric combination) and paste the Visiopt generated webhook url, then save.', image: 'img/webhook-setup/clickbank/step6.png' }
  ],
  // Onboarding renders ClickFunnels via CLICKFUNNELS_SETUP_STEPS (per conversion type). This entry
  // is only a fallback for setupStepsFor('clickfunnels') and mirrors the Classic Sales variant.
  clickfunnels: [
    { text: 'Log in to ClickFunnels and in the top menu, click on the "ClickFunnels" button, then select "Funnels".', image: 'img/webhook-setup/clickfunnels/sales/step1.png' },
    { text: 'Then select the Funnel in which you want to set the webhook.', image: 'img/webhook-setup/clickfunnels/sales/step2.png' },
    { text: 'Select "Settings" from the top menu.', image: 'img/webhook-setup/clickfunnels/sales/step3.png' },
    { text: 'On the bottom of the page in the "Webhooks" section, click on the "Manage Your Funnel Webhooks" button.', image: 'img/webhook-setup/clickfunnels/sales/step4.png' },
    { text: 'Fill out the form and create a webhook (paste in the url created in Visiopt).', image: 'img/webhook-setup/clickfunnels/sales/step5.png' }
  ],
  digistore24: [
    { text: 'Log in to digistore24 account then go to Settings → Integrations(IPN) Configuration.', image: 'img/webhook-setup/digistore24/step1.png' },
    { text: 'Scroll down and click For pros → Webhook option.', image: 'img/webhook-setup/digistore24/step2.png' },
    { text: 'Click "Add new connection" button.', image: 'img/webhook-setup/digistore24/step3.png' },
    {
      text: 'Configure the endpoint as shown, then save:',
      points: [
        'Enter a name for the webhook.',
        'Select products for which you want to send webhook.',
        'Select all as For order event.',
      ],
      image: 'img/webhook-setup/digistore24/step4.png',
    },
    { text: 'Then paste visiopt webhook url and click save.', image: 'img/webhook-setup/digistore24/step5.png' },
  ],
  gohighlevel: [
    { text: 'Log in to Go High Level and click on the Automation link in the left sidebar.', image: 'img/webhook-setup/gohighlevel/step1.png' },
    { text: 'Click on + Create Workflow button.', image: 'img/webhook-setup/gohighlevel/step2.png' },
    { text: 'For the Trigger.', image: 'img/webhook-setup/gohighlevel/step3.png' },
    { text: 'As the trigger set Order Form Submission (1), Enter a name for your workflow (2), and finally select funnel and page (3).', image: 'img/webhook-setup/gohighlevel/step4.png' },
    { text: 'As an action set up the Webhook.', image: 'img/webhook-setup/gohighlevel/step5.png' },
    {
      text: 'Configure the endpoint as shown, then save:',
      points: [
        'Enter action name.',
        'Select POST method.',
        'Paste in the URL copied from Visiopt.',
      ],
      image: 'img/webhook-setup/gohighlevel/step6.png',
    },
    { text: 'Click on the Save action button.', image: 'img/webhook-setup/gohighlevel/step7.png' },
  ],
  hubspot: [
    { text: 'Log in to your HubSpot account, click the Developer icon, and select Legacy Apps from the menu.', image: 'img/webhook-setup/hubspot/step1.png' },
    { text: 'Click on Create Legacy App.', image: 'img/webhook-setup/hubspot/step2.png' },
    { text: 'After clicking the button, a popup will appear — select "Private" as shown in the screenshot.', image: 'img/webhook-setup/hubspot/step3.png' },
    { text: 'Enter the basic information such as App Name, Scopes, and Webhook URL to configure your legacy app. You can choose any name for the app.', image: 'img/webhook-setup/hubspot/step4.png' },
    { text: 'Click "Add New Scope".', image: 'img/webhook-setup/hubspot/step5.png' },
    {
      text: 'For scopes, select the following four options:',
      points: [
        'crm.objects.deals.read',
        'crm.objects.deals.write',
        'crm.objects.contacts.read',
        'crm.objects.contacts.write',
      ],
      image: 'img/webhook-setup/hubspot/step6.png',
    },
    { text: 'In HubSpot, in the Webhook tab, paste your webhook URL (the one you copied from Visiopt) then click Commit Changes. After committing, click Continue Creating.', image: 'img/webhook-setup/hubspot/step7.png' },
    { text: 'Click “Continue Creating”.', image: 'img/webhook-setup/hubspot/step8.png' },
  ],
  typeform: [
    { text: 'Log in to your Typeform account and select the desired Typeform.', image: 'img/webhook-setup/typeform/step1.png' },
    { text: 'Go to Connect → Webhooks.', image: 'img/webhook-setup/typeform/step2.png' },
    { text: 'Click "Add a webhook".', image: 'img/webhook-setup/typeform/step3.png' },
    { text: 'Enter visiopt webhook url and click Save Webhook.', image: 'img/webhook-setup/typeform/step4.png' },
    { text: 'Turn On webhook switch.', image: 'img/webhook-setup/typeform/step5.png' }
  ],
  kajabi: [
    { text: 'Log in to your Kajabi dashboard, Settings → Site Settings → Integrations & Webhooks.', image: 'img/webhook-setup/kajabi/step1.png' },
    { text: 'Open the "webhook" tab, and click "+ Create webhook".', image: 'img/webhook-setup/kajabi/step2.png' },
    { text: 'In the webhook form, select "Payment Succeeded" as the event type. Paste the webhook URL copied from VisiOpt into the Endpoint URL field. And click Add Webhook.', image: 'img/webhook-setup/kajabi/step3.png' },
  ],
  kartra: [
    { text: 'Log in to Kartra, then in the left side bar click "My integrations".', image: 'img/webhook-setup/kartra/step1.png' },
    { text: 'Click "IPN".', image: 'img/webhook-setup/kartra/step2.png' },
    { text: 'Select "All products" and "Whole funnel", then paste in the url from Visiopt.', image: 'img/webhook-setup/kartra/step3.png' },
  ],
  konnektive: [
    { text: 'Copy your Visiopt webhook URL.', copyUrl: true },
    {
      text: 'Log in to Konnektive and in the sidebar, under the "Admin" section, click the Export link. Then click the + button to create a new profile.',
      image: 'img/webhook-setup/konnektive/step1.png',
    },
    {
      text: 'On the Create Profile screen, configure the export:',
      points: [
        'Enter a name for the profile.',
        'Select "Postback" as the Export Type.',
        'Paste the URL copied from Visiopt into the Postback URL field.',
      ],
      image: 'img/webhook-setup/konnektive/step2.png',
    },
    {
      text: 'Click "Add Field Mapping" and map each Export Name to the matching Konnektive field exactly as below. transaction_id and customer_email are required — the rest are optional but improve your reporting.',
      code:
        'Export Name            Konnektive Field\n' +
        '─────────────────────  ─────────────────\n' +
        'transaction_id         transactionId      (required)\n' +
        'customer_email         emailAddress       (required)\n' +
        'order_status           orderStatus\n' +
        'order_total            TotalPrice\n' +
        'customer_first_name    firstName\n' +
        'customer_last_name     lastName\n' +
        'customer_postalcode    postalCode\n' +
        'customer_country       country\n' +
        'customer_state         state\n' +
        'customer_city          city',
      image: 'img/webhook-setup/konnektive/step3.png',
    },
    {
      text: 'Finally, create a routing rule so Konnektive knows which orders to send:',
      points: [
        'Click the + button and select the profile you just created.',
        'Choose the customer types to export — New Sales, Upsells and/or Recurring Billing.',
        'Select the campaign and product this funnel uses (or "All").',
      ],
    },
  ],
  ontraport: [
    { text: 'Log in to Ontraport, then in the top menu select "Automations" and then click on the "Rules" option.', image: 'img/webhook-setup/ontraport/step1.png' },
    { text: 'Click on "+ New Rule".', image: 'img/webhook-setup/ontraport/step2.png' },
    { text: 'Then click on the "+ Add Trigger" button.', image: 'img/webhook-setup/ontraport/step3.png' },
    {
      text: 'Select Product to trigger webhook:',
      points: [
        'Each test and / or each unique campaign will use a different webhook.',
        'If you have multiple products in a single test or campaign that you want to track and trigger a webhook for then choose "All Products".',
        'This will trigger the same webhook regardless of which product is purchased.',
        'If you are tracking different tests and campaigns and are creating more than 1 webhook inside of Visiopt than you can create a new webhook rule for each product as shown here.',
      ],
      image: 'img/webhook-setup/ontraport/step4.png',
    },
    { text: 'Then under the "Sales" section, select the "Purchase Product" option.', image: 'img/webhook-setup/ontraport/step5.png' },
    { text: 'Click on the "+ Add Action" button.', image: 'img/webhook-setup/ontraport/step6.png' },
    { text: 'Then under the "Sites / Pages" section, select on the "Send to a Webhook" option.', image: 'img/webhook-setup/ontraport/step7.png' },
    { text: 'In the "Send to a Webhook" section paste in the webhook url copied from Visiopt.', image: 'img/webhook-setup/ontraport/step8.png' },
    { text: 'In the "Select webhook method" section select "post".', image: 'img/webhook-setup/ontraport/step9.png' },
    { text: 'In the "Add the data to send" paste in the following (without the quotation marks): "email=[Email]&transaction_id=[Last Invoice #]&price=[Last Charge Amount]".', image: 'img/webhook-setup/ontraport/step10.png' },
  ],
  paykickstart: [
    { text: 'Copy your Visiopt webhook URL.', copyUrl: true },
    {
      text: 'Log in to Paykickstart, open the product you want to track and go to Edit → Integrations, then set ENABLE IPN to "Enable".',
      image: 'img/webhook-setup/paykickstart/step1.png',
    },
    {
      text: 'Configure the IPN as shown, then add it:',
      points: [
        'Paste the URL copied from Visiopt into the IPN URL field.',
        'Select "All Events" in the SET EVENT dropdown.',
        'Click the "+ Add" button.',
      ],
      image: 'img/webhook-setup/paykickstart/step2.png',
    },
    { text: 'Save the changes on the product — that\'s it.', image: 'img/webhook-setup/paykickstart/step3.png' },
  ],
  samcart: [
    { text: 'Log in to Samcart then in the top right click the "Settings" icon > the click "Integrations".', image: 'img/webhook-setup/samcart/step1.png' },
    { text: 'Click the "New Integration" button, then from the dropdown select "Notify URL".', image: 'img/webhook-setup/samcart/step2.png' },
    {
      text: 'Configure the integration as shown, then save:',
      points: [
        'Enter integration name.',
        'Paste in the URL copied from Visiopt.',
      ],
      image: 'img/webhook-setup/samcart/step3.png',
    },
    { text: 'Then click the "New Rule" button, select "eCommerce" and "Product Purchased".', image: 'img/webhook-setup/samcart/step4.png' },
    { text: 'When done hit "Submit".', image: 'img/webhook-setup/samcart/step5.png' },
  ],
  shopify: [
    { text: 'Log in to Shopify, and click on the settings in the bottom of the left sidebar.', image: 'img/webhook-setup/shopify/step1.png' },
    { text: 'Click on "Notifications".', image: 'img/webhook-setup/shopify/step2.png' },
    { text: 'Click on the "Create webhook" button.', image: 'img/webhook-setup/shopify/step3.png' },
    {
      text: 'Configure the webhook as shown, then save:',
      points: [
        'Select "Order payment" as Event.',
        'Select JSON as Format.',
        'Paste in the URL copied from Visiopt.',
      ],
      image: 'img/webhook-setup/shopify/step4.png',
    }
  ],
  stealthseminar: [
    { text: 'Go to your StealthSeminar account, open the webinar where you want to set up the webhook, and click the Edit button. Navigate to the Plugins tab → Click on Stealth Power Webhook → Click Add Connection.', image: 'img/webhook-setup/stealthseminar/step1.png' },
    {
      text: 'Configure the webhook as shown, then save:',
      points: [
        'Enter webhook name.',
        'Paste in the URL copied from Visiopt.',
      ],
      image: 'img/webhook-setup/stealthseminar/step2.png',
    },
    { text: 'You’ll see the following screen. Click Toggle All and then click Save and Apply.', image: 'img/webhook-setup/stealthseminar/step3.png' },
    { text: 'After saving, go back to the Webinar List page → Click Edit again → Go to the Plugins tab. You’ll now see the webhook setup as shown here.', image: 'img/webhook-setup/stealthseminar/step4.png' },
    {
      text: 'Click View, and while updating the Countdown Variation webhook:',
      points: [
        'Select Event = They Register if you want to track Registration.',
        'Select Event = They Attend the Webinar if you want to track Attendance.',
      ],
      image: 'img/webhook-setup/stealthseminar/step5.png',
    },
  ],
  ultracart: [
    { text: 'Log in to UltraCart and open Advanced → Developer Tools → Webhooks from the left sidebar.', image: 'img/webhook-setup/ultracart/step1.png' },
    { text: 'Click "Setup a Webhook".', image: 'img/webhook-setup/ultracart/step2.png' },
    { text: 'Click "New Webhook".', image: 'img/webhook-setup/ultracart/step3.png' },
    {
      text: 'Enter the webhook details:',
      points: [
        'Enter a webhook name.',
        'Paste the Target URL copied from Visiopt.',
        'Select the latest API version.',
      ],
      image: 'img/webhook-setup/ultracart/step4.png',
    },
    {
      text: 'Configure the event subscriptions, then create the webhook:',
      points: [
        'Turn on the "Order" option.',
        'Turn on the "Order Create" option.',
        'Add Item, Summary, Billing and Shipping as expansion options.',
        'Click "Create Webhook" to save.',
      ],
      image: 'img/webhook-setup/ultracart/step5.png',
    },
  ],
  thrivecart: [
    { text: 'Log in to Thrive Cart and select Settings from the top right drop-down menu.', image: 'img/webhook-setup/thrivecart/step1.png' },
    { text: 'Click on the "API & Webhooks" button.', image: 'img/webhook-setup/thrivecart/step2.png' },
    { text: 'Click on the "View settings" button in the "Webhooks & notifications" section.', image: 'img/webhook-setup/thrivecart/step3.png' },
    { text: 'Click on the "+ Add another webhook" button.', image: 'img/webhook-setup/thrivecart/step4.png' },
    {
      text: 'Configure the webhook as shown, then save:',
      points: [
        'Enter webhook name.',
        'Paste in the URL copied from Visiopt.',
        'Check the "Receive results as JSON" checkbox.'
      ],
      image: 'img/webhook-setup/thrivecart/step5.png',
    },
  ],
  woocommerce: [
    { text: 'Log in to WooCommerce then in the left sidebar click on the "WooCommerce" section to expand > then click "Settings" > then select the "Advanced" tab.', image: 'img/webhook-setup/woocommerce/step1.png' },
    { text: 'Click "Add webhook" button.', image: 'img/webhook-setup/woocommerce/step2.png' },
    {
      text: 'Configure the webhook as shown, then save:',
      points: [
        'Set status as active.',
        'Select "Order created" as topic.',
        'Paste in the URL copied from Visiopt.',
      ],
      image: 'img/webhook-setup/woocommerce/step3.png',
    },
  ],
  youform: [
    { text: 'Log in to your youform account, select the desired form.', image: 'img/webhook-setup/youform/step1.png' },
    { text: 'Navigate to Integrate → Webhooks → Connect Button.', image: 'img/webhook-setup/youform/step2.png' },
    {
      text: 'Paste in the URL copied from Visiopt, then save:',
      image: 'img/webhook-setup/youform/step3.png',
    }
  ],
  customintegration: [
    { text: 'Copy your Visiopt webhook URL — your cart will POST its order data to it.', copyUrl: true },
    {
      text: 'When an order completes, send a POST request to that URL with a JSON body. Only three fields are required:',
      points: [
        'transaction_id — your unique order / transaction id',
        'sales_amount — the order total, e.g. 52.50',
        'email — the buyer\'s email address',
      ],
      code: '{\n  "transaction_id": "1111111",\n  "sales_amount": "52.50",\n  "email": "test@gmail.com"\n}',
    },
    {
      text: 'Every other field is optional, but sending them gives you richer reporting:',
      points: [
        'transaction_type — "paid" (default) or "refunded"',
        'first_name, last_name, phone',
        'zipcode, city, state, country',
        'ip_address — the buyer\'s IP address',
      ],
      code:
        '{\n  "transaction_id": "1111111",\n  "sales_amount": "52.50",\n  "transaction_type": "paid",\n  "email": "test@gmail.com",\n  "first_name": "test1",\n  "last_name": "test2",\n  "zipcode": "45321",\n  "country": "US",\n  "state": "LA",\n  "city": "Gonzales",\n  "ip_address": "12:15:15:18",\n  "phone": "+14155552671"\n}',
    },
    {
      text: 'Optional (recommended): for more accurate attribution, read Visiopt\'s tracking cookies on your landing page and include them in the webhook body. If you skip them, Visiopt matches the sale to a test and campaign by the buyer\'s email.',
      points: [
        '_kx_vt (optional) — the test variation the visitor saw',
        '_kx_sid (optional) — the visitor session id',
        '_n_sid_random (optional) — the visitor id',
      ],
      code:
        "<script type='text/javascript'>\nfunction getVisiCookie3(name) {\n  var v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');\n  return v ? v[2] : null;\n}\nvar vtss_kx = setInterval(function () {\n  if (typeof _kx_called != 'undefined' && _kx_called == true) {\n    var v_kx_vt = getVisiCookie3('_kx_vt');\n    var v_kx_sid = getVisiCookie3('_kx_sid');\n    var v_n_sid_random = getVisiCookie3('_n_sid_random');\n    // store these and send them in your webhook body\n    clearInterval(vtss_kx);\n  }\n}, 50);\n</script>",
    },
    {
      text: 'Refunds: post the same transaction_id as the original payment, with transaction_type set to "refunded".',
      code: '{\n  "transaction_id": "1111111",\n  "sales_amount": "52.50",\n  "email": "test@gmail.com",\n  "transaction_type": "refunded"\n}',
    },
    { text: 'As soon as Visiopt receives a correctly formatted POST, your sales start tracking — no further setup is required.' },
  ],
};

// Resolve the guided steps for an integration value
export const setupStepsFor = (value: string): SetupStep[] =>
  SETUP_STEPS_BY_INTEGRATION[value] || DEFAULT_SETUP_STEPS;

export const CLICKFUNNELS_SETUP_STEPS: Record<string, SetupStep[]> = {
  // ── Sales · ClickFunnels Classic (event: purchase_created) ──
  sales: [
    { text: 'In the ClickFunnels interface, in the top menu click the "ClickFunnels" button, then select "Funnels".', image: 'img/webhook-setup/clickfunnels/sales/step1.png' },
    { text: 'Then select the funnel in which you want to set the webhook.', image: 'img/webhook-setup/clickfunnels/sales/step2.png' },
    { text: 'Select "Settings" from the top menu.', image: 'img/webhook-setup/clickfunnels/sales/step3.png' },
    { text: 'At the bottom of the page, in the "Webhooks" section, click the "Manage Your Funnel Webhooks" button.', image: 'img/webhook-setup/clickfunnels/sales/step4.png' },
    {
      text: 'Fill out the form and create the webhook (paste in the URL created in Visiopt):',
      points: [
        'Enter the URL copied from Visiopt.',
        'Select "purchase_created" as the event.',
        'Click "Create Funnel Webhook".',
      ],
      image: 'img/webhook-setup/clickfunnels/sales/step5.png',
    },
  ],
  // ── Opt-in · ClickFunnels Classic (event: contact_created) ──
  optin: [
    { text: 'In the ClickFunnels interface, in the top menu click the "ClickFunnels" button, then select "Funnels".', image: 'img/webhook-setup/clickfunnels/optin/step1.png' },
    { text: 'Then select the funnel in which you want to set the webhook.', image: 'img/webhook-setup/clickfunnels/optin/step2.png' },
    { text: 'Select "Settings" from the top menu.', image: 'img/webhook-setup/clickfunnels/optin/step3.png' },
    { text: 'At the bottom of the page, in the "Webhooks" section, click the "Manage Your Funnel Webhooks" button.', image: 'img/webhook-setup/clickfunnels/optin/step4.png' },
    {
      text: 'Fill out the form and create the webhook (paste in the URL created in Visiopt):',
      points: [
        'Enter the URL copied from Visiopt.',
        'Select "contact_created" as the event.',
        'Click "Create Funnel Webhook".',
      ],
      image: 'img/webhook-setup/clickfunnels/optin/step5.png',
    },
  ],
  // ── Sales · ClickFunnels 2.0 (event: order.completed) ──
  sales_2: [
    { text: 'Log in to your ClickFunnels account and click "Workspace Settings" at the bottom left.', image: 'img/webhook-setup/clickfunnels/sales_2/step1.png' },
    { text: 'Click the "Webhooks" option.', image: 'img/webhook-setup/clickfunnels/sales_2/step2.png' },
    { text: 'Click the "Add endpoint" button.', image: 'img/webhook-setup/clickfunnels/sales_2/step3.png' },
    {
      text: 'Enter the following details, then create the endpoint:',
      points: [
        'Enter a name for the webhook.',
        'Enter the URL copied from Visiopt.',
        'Select the "order.completed" event under Event types.',
        'Click "Create endpoint".',
      ],
      image: 'img/webhook-setup/clickfunnels/sales_2/step4.png',
    },
  ],
  // ── Opt-in · ClickFunnels 2.0 (event: contact.created) ──
  optin_2: [
    { text: 'Log in to your ClickFunnels account and click "Workspace Settings" at the bottom left.', image: 'img/webhook-setup/clickfunnels/optin_2/step1.png' },
    { text: 'Click the "Webhooks" option.', image: 'img/webhook-setup/clickfunnels/optin_2/step2.png' },
    { text: 'Click the "Add endpoint" button.', image: 'img/webhook-setup/clickfunnels/optin_2/step3.png' },
    {
      text: 'Enter the following details, then create the endpoint:',
      points: [
        'Enter a name for the webhook.',
        'Enter the URL copied from Visiopt.',
        'Select the "contact.created" event under Event types.',
        'Click "Create endpoint".',
      ],
      image: 'img/webhook-setup/clickfunnels/optin_2/step4.png',
    },
  ],
};

// Manual steps for ClickFunnels, chosen by the selected conversion type (falls back to Classic Sales)
export const clickfunnelsSetupSteps = (conversionType: string): SetupStep[] =>
  CLICKFUNNELS_SETUP_STEPS[conversionType] || CLICKFUNNELS_SETUP_STEPS.sales;

// Extra credential a processor needs before its webhook can work
// Per-provider webhook credential fields + "how to get them" help live in the shared config,
// so Settings (Payment Processors & Carts) and this onboarding step never drift. Re-exported
// under the onboarding names so existing imports keep working.
export type { IntegrationField, IntegrationFieldHelp } from '../../components/GeneralComponent/webhookIntegrationFields';
export {
  integrationFieldsFor as credentialFieldsFor,
  credentialsRequiredFor,
  canAutoCreate,
  AUTO_CREATE_PROCESSORS,
  CONVERSION_TYPE_PROCESSORS,
  REQUIRED_CREDENTIAL_PROCESSORS,
  AUTO_CREATE_HELPER_TEXT,
  REQUIRED_CREDENTIAL_HELPER_TEXT,
} from '../../components/GeneralComponent/webhookIntegrationFields';

